/**
 * 中継サーバーとのライブ接続。
 *
 * gv-core の `RelayClient`（送受信）と `StreamDecoder`（解析）を繋ぎ、
 * 端末ごとの最新位置と航跡を保持する。
 *
 * ⚠️ ブラウザから**本物の中継サーバーへ直接は接続できない**（docs/05）。
 *    CORS ヘッダが無く、アプリを HTTPS で配信すると混在コンテンツで遮断されるため。
 *    実運用では GV Bridge を挟む。ここでは開発用ダミーサーバーで動作を確認する。
 */

import {
  RelayClient,
  StreamDecoder,
  isPlausibleLiveFixTime,
  type CircleArea,
  type FixSample,
  type MarkPoint,
} from '@gv4d/core';
import { isNewerFix } from './dedupe.ts';

export interface LiveTerminal {
  terminalId: string;
  /** 受信した測位の履歴（航跡） */
  fixes: FixSample[];
  latest: FixSample;
  /**
   * `latest`が「未来方向にもっともらしい」時刻の点から採用されたものか。
   * false のときは、次に届いたもっともらしい点を時刻の前後に関わらず
   * 無条件に採用し直す（`apps/bridge/src/state.ts`の同名フィールド参照）。
   */
  latestIsPlausible: boolean;
  /** 最後にデータが届いた時刻 */
  updatedAt: number;
}

export interface LiveSnapshot {
  terminals: LiveTerminal[];
  marks: Map<string, MarkPoint[]>;
  markNames: Map<string, string[]>;
  circles: Map<string, CircleArea[]>;
  /** 解釈できなかった行の数。他クライアントの独自センテンスなど */
  passthroughCount: number;
  /** 直近の受信バイト数 */
  lastBytes: number;
  polls: number;
}

export type LiveStatus = 'idle' | 'connecting' | 'connected' | 'error';

export interface LiveOptions {
  serverUrl: string;
  pollingMs: number;
  /** UTM ゾーン。$_TVMARK / $_TVCIRCLE の復元に必要 */
  zone?: number;
  /** 1端末あたりに保持する航跡点の上限 */
  maxTrackPoints?: number;
}

export class LiveConnection {
  #client: RelayClient;
  #decoder = new StreamDecoder();
  #terminals = new Map<string, LiveTerminal>();
  #marks = new Map<string, MarkPoint[]>();
  #markNames = new Map<string, string[]>();
  #circles = new Map<string, CircleArea[]>();
  #zone?: number;
  #maxTrackPoints: number;
  #passthroughCount = 0;
  #lastBytes = 0;
  #polls = 0;

  status: LiveStatus = 'idle';
  onUpdate: ((snapshot: LiveSnapshot) => void) | null = null;
  onStatus: ((status: LiveStatus, message: string) => void) | null = null;

  constructor(options: LiveOptions) {
    this.#zone = options.zone;
    this.#maxTrackPoints = options.maxTrackPoints ?? 5000;
    this.#client = new RelayClient({
      serverUrl: options.serverUrl,
      pollingMs: options.pollingMs,
    });
    this.#client.onData = (data) => this.#ingest(data);
    this.#client.onError = (e) => {
      this.status = 'error';
      this.onStatus?.('error', (e as Error).message);
    };
  }

  #ingest(data: string): void {
    this.#lastBytes = data.length;
    this.#polls++;

    const result = this.#decoder.decode(data, { zone: this.#zone });
    this.#passthroughCount += result.passthrough.length;

    for (const fix of result.fixes) {
      // 実際より先の時刻を持つ点は、そもそも端末の状態に触れさせない
      // （dedupe.ts参照。latestの基準として採用してしまうと、以後の
      // 正しい時刻の点が全部「基準より古い」と判定され捨てられ続ける）
      if (!isPlausibleLiveFixTime(fix.t)) continue;

      let t = this.#terminals.get(fix.terminalId);
      if (!t) {
        // ここに来る時点でfixは既にisPlausibleLiveFixTimeを通過済み
        t = { terminalId: fix.terminalId, fixes: [], latest: fix, latestIsPlausible: true, updatedAt: Date.now() };
        this.#terminals.set(fix.terminalId, t);
      }
      // 再送された古い点が紛れ込むと航跡が一瞬過去へ巻き戻って見えるため、
      // 直前に採用した点より新しい時刻のものだけ追加する（dedupe.ts参照）
      if (isNewerFix(t.fixes[t.fixes.length - 1], fix)) {
        t.fixes.push(fix);
        if (t.fixes.length > this.#maxTrackPoints) {
          t.fixes.splice(0, t.fixes.length - this.#maxTrackPoints);
        }
      }
      if (!t.latestIsPlausible || fix.t >= t.latest.t) {
        t.latest = fix;
        t.latestIsPlausible = true;
      }
      t.updatedAt = Date.now();
    }

    for (const [tid, marks] of result.marks) this.#marks.set(tid, marks);
    for (const [tid, names] of result.markNames) this.#markNames.set(tid, names);
    for (const [tid, circles] of result.circles) this.#circles.set(tid, circles);

    if (this.status !== 'connected') {
      this.status = 'connected';
      this.onStatus?.('connected', '接続しました');
    }
    this.onUpdate?.(this.snapshot());
  }

  snapshot(): LiveSnapshot {
    return {
      terminals: [...this.#terminals.values()].sort((a, b) =>
        a.terminalId.localeCompare(b.terminalId),
      ),
      marks: this.#marks,
      markNames: this.#markNames,
      circles: this.#circles,
      passthroughCount: this.#passthroughCount,
      lastBytes: this.#lastBytes,
      polls: this.#polls,
    };
  }

  /** 送信キューに積む（自機位置の共有など） */
  send(line: string): void {
    this.#client.send(line);
  }

  start(): void {
    this.status = 'connecting';
    this.onStatus?.('connecting', '接続中…');
    this.#client.start();
  }

  stop(): void {
    this.#client.stop();
    this.status = 'idle';
    this.onStatus?.('idle', '切断しました');
  }

  /** サーバー側カーソル。0 なら次回に全履歴を取り直す */
  get gotpos(): number {
    return this.#client.gotpos;
  }
}
